import CreateIcon from '@mui/icons-material/Create';
import { Divider, LinearProgress } from '@mui/material';
import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useLogoutMutation, UserProfileDefaultFields } from 'api';
import { Avatar, AvatarEditor, NewConfirmDialog } from 'components';
import { UserFieldMapping } from 'features/employeeDetails/containers/EmployeeExtendedDetailsPage/adapter';
import { useAuth, useModal, useWindowSize } from 'hooks';
import { Links } from 'settings';
import theme from 'styles/theme';
import { UserExtendedDetails, UserProfileField } from 'types';
import { getFullName } from 'utils';

import { ProfileCardMeta } from '../ProfileCardMeta';
import { MAX_AVATAR_SIZE } from './constants';
import { useProfileCard } from './hooks';
import {
  CardW,
  EditButtonW,
  HeaderRow,
  MobileActionsW,
  // MobileCameraIcon,
  MoreW,
  NameSection,
  NameText,
  PositionText,
  TextW,
} from './styled';

interface ProfileCardProps {
  privateView?: boolean;
  handleOpenModal: () => void;
  user: UserExtendedDetails;
  userProfileFields: UserProfileField[];
}

interface FilteredInfo {
  [key: string]: string;
}

export function ProfileCard({
  privateView = true,
  handleOpenModal,
  user,
  userProfileFields,
}: ProfileCardProps): ReactElement {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logoutMutation } = useLogoutMutation();
  const { handleAvatarFile, isLoading: isLoadingAvatarUpdate } = useProfileCard();

  const { isManager, user: currentUser, removeCurrentUser } = useAuth();
  const { isMobile } = useWindowSize();
  const {
    handleCloseModal: closeConfirmLogoutModal,
    isOpen: isOpenConfirmLogoutModal,
    handleOpenModal: openConfirmLogoutModal,
  } = useModal();
  const params = useParams();
  const { pathname } = useLocation();
  const [filteredData, setFilteredData] = useState<FilteredInfo>({});

  useEffect(() => {
    const newFilteredData: FilteredInfo = {};

    userProfileFields
      ?.filter((field) => field.category === 'public')
      .forEach((field) => {
        if (field.label === UserProfileDefaultFields.NAME) {
          newFilteredData['firstName'] = user.firstName;
          newFilteredData['lastName'] = user.lastName;
        }

        const key: string = UserFieldMapping[field.label];
        if (key && key in user) {
          newFilteredData[key] = user[key as keyof typeof user] as string;
        }
      });

    setFilteredData(newFilteredData);
  }, [userProfileFields, user.custom_profile_fields, user]);

  const { id, avatar, countryCode } = user;

  const {
    firstName = '',
    lastName = '',
    phone = '',
    email = '',
    city = '',
    position = '',
    address1 = '',
    birthDate = '',
    postcode = '',
    address2 = '',
    region = '',
    country = '',
  } = privateView
    ? {
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone,
        email: user.email,
        city: user.city,
        position: user.position,
        address1: user.address1,
        birthDate: user.birthDate || '',
        postcode: user.postcode,
        address2: user.address2,
        region: user.region,
        country: user.country,
      }
    : filteredData;

  const isCurrentUser = id === currentUser?.id;
  const fullName = getFullName({ firstName, lastName });
  const isLogoutBtnVisible = privateView && id === currentUser?.id;

  async function handleConfirmLogout() {
    await logoutMutation();
    removeCurrentUser();
    closeConfirmLogoutModal();
    navigate(Links.auth.signIn);
    if (Notification.permission === 'granted') {
      window.location.reload();
    }
  }

  const extendedDetailsLink = !params?.employeeId
    ? Links.profile.extended.extended
    : pathname.includes('resources')
    ? Links.resources.employees.employee.extended
    : Links.we.employees.employee.extended;

  function goToExtendedDetails() {
    navigate(
      generatePath(extendedDetailsLink, {
        employeeId: id,
      }),
    );
  }

  return (
    <CardW privateView={privateView}>
      {isLoadingAvatarUpdate && (
        <LinearProgress
          color="info"
          sx={{ position: 'absolute', zIndex: 10, top: 0, width: '100%', left: 0 }}
        />
      )}

      <HeaderRow>
        <AvatarEditor
          multiple={false}
          acceptFormats="image/*"
          handleFile={handleAvatarFile}
          disabled={!isCurrentUser}
          maxAvatarSize={MAX_AVATAR_SIZE}
        >
          <Avatar
            size="104"
            isAvailableToUpload={isCurrentUser}
            userId={id}
            filename={avatar?.small || ''}
            withNavigation={false}
            wrapperSx={{ '& .MuiAvatar-root': { border: `1px solid ${theme.palette.info.light}` } }}
          />
        </AvatarEditor>

        <NameSection>
          <NameText>{fullName}</NameText>
          {position && <PositionText>{position}</PositionText>}
        </NameSection>

        {!isMobile && privateView && isCurrentUser && (
          <EditButtonW
            startIcon={<CreateIcon style={{ fill: 'white' }} />}
            style={{
              height: '56px',
              padding: '16px',
              fontSize: '14px',
              width: '200px',
              backgroundColor: theme.palette.info.main,
              color: '#fff',
              borderRadius: '8px',
              fontWeight: 400,
              textTransform: 'none',
            }}
            onClick={handleOpenModal}
          >
            {t('profile.edit.profile')}
          </EditButtonW>
        )}
        {!isMobile && isLogoutBtnVisible && (
          <EditButtonW
            style={{
              height: '56px',
              padding: '16px',
              width: '150px',
              fontSize: '14px',
              backgroundColor: theme.palette.white.main,
              color: theme.palette.primary.main,
              border: '1px solid #dddddd',
              borderRadius: '8px',
              fontWeight: 400,
              textTransform: 'none',
            }}
            onClick={openConfirmLogoutModal}
          >
            {t('profile.logout.text')}
          </EditButtonW>
        )}
      </HeaderRow>

      <Divider sx={{ my: '16px' }} />

      <ProfileCardMeta
        phone={phone}
        email={email}
        address1={address1}
        address2={address2}
        city={city}
        region={region}
        country={country}
        postcode={postcode}
        countryCode={countryCode}
        birthDate={birthDate}
      />

      {isMobile && (
        <MobileActionsW>
          {privateView && isCurrentUser && (
            <EditButtonW
              startIcon={<CreateIcon style={{ fill: 'white' }} />}
              style={{
                height: '56px',
                width: '100%',
                fontSize: '14px',
                backgroundColor: theme.palette.info.main,
                color: '#fff',
                borderRadius: '8px',
                fontWeight: 400,
                textTransform: 'none',
              }}
              onClick={handleOpenModal}
            >
              {t('profile.edit.profile')}
            </EditButtonW>
          )}
          {isLogoutBtnVisible && (
            <EditButtonW
              style={{
                height: '56px',
                width: '100%',
                fontSize: '14px',
                backgroundColor: theme.palette.white.main,
                color: theme.palette.primary.main,
                border: '1px solid #dddddd',
                borderRadius: '8px',
                fontWeight: 400,
                textTransform: 'none',
              }}
              onClick={openConfirmLogoutModal}
            >
              {t('profile.logout.text')}
            </EditButtonW>
          )}
        </MobileActionsW>
      )}

      {(isManager || isCurrentUser) && (
        <MoreW onClick={goToExtendedDetails}>
          <TextW>{t('common.expand')}</TextW>
        </MoreW>
      )}
      <NewConfirmDialog
        title={t('profile.logout.confirmText')}
        subTitle={t('profile.logout.subText')}
        open={isOpenConfirmLogoutModal}
        onConfirm={handleConfirmLogout}
        onClose={closeConfirmLogoutModal}
        submitLabel={t('profile.logout.confirmYes')}
        cancelLabel={t('profile.logout.confirmNo')}
      />
    </CardW>
  );
}
